import { useState, useMemo } from "react";
import { startOfMonth, endOfMonth, startOfWeek, endOfWeek, startOfDay, endOfDay, addDays, addMonths, addWeeks, format, isSameMonth, isSameDay, eachDayOfInterval, parseISO } from "date-fns";
import { CaretLeft, CaretRight, Warning } from "@phosphor-icons/react";
import { useTasksCalendar } from "./hooks/useTasksCalendar";

const PRIORITY_COLORS = {
  P0: "#DC2626", // red - critical/urgent
  P1: "#DC2626", // red - high
  P2: "#F59E0B", // amber - medium
  P3: "#10B981", // green - low
};

const eventColor = (event) => {
  if (event.status === "completed" || event.status === "cancelled") return "#94A3B8"; // grey
  return PRIORITY_COLORS[event.priority_grade] || "#475569";
};

const VIEWS = ["month", "week", "day"];

export default function TaskCalendar({ user, onEventClick, view: viewProp, onViewChange, filters }) {
  const [cursor, setCursor] = useState(() => new Date());
  const view = viewProp || "month";

  // Compute window
  const { fromDate, toDate, gridStart, gridEnd, days } = useMemo(() => {
    if (view === "day") {
      const s = startOfDay(cursor); const e = endOfDay(cursor);
      return { fromDate: format(s, "yyyy-MM-dd"), toDate: format(e, "yyyy-MM-dd"), gridStart: s, gridEnd: e, days: [s] };
    }
    if (view === "week") {
      const s = startOfWeek(cursor, { weekStartsOn: 1 });
      const e = endOfWeek(cursor, { weekStartsOn: 1 });
      return { fromDate: format(s, "yyyy-MM-dd"), toDate: format(e, "yyyy-MM-dd"), gridStart: s, gridEnd: e, days: eachDayOfInterval({ start: s, end: e }) };
    }
    // month
    const mStart = startOfMonth(cursor); const mEnd = endOfMonth(cursor);
    const s = startOfWeek(mStart, { weekStartsOn: 1 });
    const e = endOfWeek(mEnd, { weekStartsOn: 1 });
    return { fromDate: format(s, "yyyy-MM-dd"), toDate: format(e, "yyyy-MM-dd"), gridStart: s, gridEnd: e, days: eachDayOfInterval({ start: s, end: e }) };
  }, [cursor, view]);

  const { events, loading } = useTasksCalendar(user, {
    fromDate, toDate,
    view: filters?.viewPreset || "",
    priorityGrade: filters?.priorityGrade || "",
    department: filters?.department || "",
    relatedModule: filters?.relatedModule || "",
  });

  const eventsByDay = useMemo(() => {
    const map = new Map();
    for (const ev of events) {
      if (!ev.due_at) continue;
      let d;
      try { d = parseISO(ev.due_at); } catch (e) { continue; }
      const key = format(d, "yyyy-MM-dd");
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(ev);
    }
    // Sort within each day by due_at ascending
    for (const arr of map.values()) arr.sort((a, b) => (a.due_at || "").localeCompare(b.due_at || ""));
    return map;
  }, [events]);

  const navigate = (dir) => {
    if (view === "day") setCursor(addDays(cursor, dir));
    else if (view === "week") setCursor(addWeeks(cursor, dir));
    else setCursor(addMonths(cursor, dir));
  };

  const headerLabel = useMemo(() => {
    if (view === "day") return format(cursor, "EEEE, dd MMM yyyy");
    if (view === "week") return `${format(gridStart, "dd MMM")} – ${format(gridEnd, "dd MMM yyyy")}`;
    return format(cursor, "MMMM yyyy");
  }, [cursor, view, gridStart, gridEnd]);

  return (
    <div data-testid="task-calendar" className="bg-white border border-slate-200 rounded-none">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <button data-testid="calendar-prev" onClick={() => navigate(-1)} className="p-1 hover:bg-slate-100 rounded-none" aria-label="Previous"><CaretLeft size={16} /></button>
          <button data-testid="calendar-today" onClick={() => setCursor(new Date())} className="text-xs uppercase tracking-wider font-bold px-3 py-1 border border-slate-300 hover:bg-slate-50">Today</button>
          <button data-testid="calendar-next" onClick={() => navigate(1)} className="p-1 hover:bg-slate-100 rounded-none" aria-label="Next"><CaretRight size={16} /></button>
          <h3 className="font-semibold text-sm ml-2" data-testid="calendar-label">{headerLabel}</h3>
        </div>
        <div className="flex items-center gap-1">
          {VIEWS.map((v) => (
            <button
              key={v}
              data-testid={`calendar-view-${v}`}
              onClick={() => onViewChange(v)}
              className={`text-xs uppercase tracking-wider font-bold px-3 py-1 border ${view === v ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-slate-300 hover:bg-slate-50"}`}
            >
              {v}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="loading-spinner"></div></div>
      ) : view === "month" ? (
        <MonthGrid days={days} cursor={cursor} eventsByDay={eventsByDay} onEventClick={onEventClick} />
      ) : view === "week" ? (
        <WeekGrid days={days} eventsByDay={eventsByDay} onEventClick={onEventClick} />
      ) : (
        <DayGrid day={cursor} events={eventsByDay.get(format(cursor, "yyyy-MM-dd")) || []} onEventClick={onEventClick} />
      )}
    </div>
  );
}

function EventChip({ event, onClick, dense }) {
  const c = eventColor(event);
  return (
    <button
      type="button"
      onClick={() => onClick && onClick(event)}
      data-testid={`calendar-event-${event.task_id}`}
      className={`w-full text-left rounded-none truncate inline-flex items-center gap-1 hover:opacity-90 transition-opacity ${dense ? "text-[10px] px-1 py-0.5" : "text-xs px-1.5 py-1"}`}
      style={{
        backgroundColor: `${c}15`, color: c,
        borderLeft: `3px solid ${c}`,
        borderTop: event.is_overdue ? `1px solid #DC2626` : "none",
        borderRight: event.is_overdue ? `1px solid #DC2626` : "none",
        borderBottom: event.is_overdue ? `1px solid #DC2626` : "none",
      }}
      title={`${event.title} · ${event.due_time || ""}${event.assignee_names?.length ? " · " + event.assignee_names.join(", ") : ""}`}
    >
      {event.is_overdue && <Warning size={10} weight="bold" />}
      {event.due_time && <span className="font-mono">{event.due_time}</span>}
      <span className="truncate font-semibold">{event.title}</span>
    </button>
  );
}

function MonthGrid({ days, cursor, eventsByDay, onEventClick }) {
  const weekdays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  return (
    <div data-testid="calendar-month-grid">
      <div className="grid grid-cols-7 border-b border-slate-200">
        {weekdays.map((d) => (
          <div key={d} className="px-2 py-2 text-xs uppercase tracking-wider font-bold text-slate-500 border-r border-slate-200 last:border-r-0">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7" style={{ gridAutoRows: "min-content" }}>
        {days.map((d) => {
          const key = format(d, "yyyy-MM-dd");
          const dayEvents = eventsByDay.get(key) || [];
          const inMonth = isSameMonth(d, cursor);
          const isToday = isSameDay(d, new Date());
          return (
            <div
              key={key}
              data-testid={`calendar-day-${key}`}
              className={`min-h-[110px] border-r border-b border-slate-200 last:border-r-0 p-1 ${inMonth ? "bg-white" : "bg-slate-50/50"}`}
            >
              <div className={`text-xs font-semibold mb-1 ${inMonth ? (isToday ? "text-[#002FA7]" : "text-slate-700") : "text-slate-400"}`}>
                {format(d, "d")}
                {isToday && <span className="ml-1 text-[9px] uppercase tracking-wider text-[#002FA7] font-bold">Today</span>}
              </div>
              <div className="space-y-0.5 max-h-[90px] overflow-y-auto">
                {dayEvents.slice(0, 4).map((ev) => (
                  <EventChip key={ev.task_id} event={ev} onClick={onEventClick} dense />
                ))}
                {dayEvents.length > 4 && (
                  <div className="text-[10px] text-slate-500 px-1">+{dayEvents.length - 4} more</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WeekGrid({ days, eventsByDay, onEventClick }) {
  return (
    <div data-testid="calendar-week-grid" className="grid grid-cols-7" style={{ gridAutoRows: "min-content" }}>
      {days.map((d) => {
        const key = format(d, "yyyy-MM-dd");
        const dayEvents = eventsByDay.get(key) || [];
        const isToday = isSameDay(d, new Date());
        return (
          <div key={key} data-testid={`calendar-day-${key}`} className="min-h-[400px] border-r border-slate-200 last:border-r-0 p-2">
            <div className={`text-xs font-bold uppercase tracking-wider mb-2 ${isToday ? "text-[#002FA7]" : "text-slate-600"}`}>
              {format(d, "EEE")} · {format(d, "dd")}
            </div>
            <div className="space-y-1">
              {dayEvents.map((ev) => (
                <EventChip key={ev.task_id} event={ev} onClick={onEventClick} />
              ))}
              {dayEvents.length === 0 && <div className="text-[10px] text-slate-300 italic">No tasks</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DayGrid({ day, events, onEventClick }) {
  return (
    <div data-testid="calendar-day-grid" className="p-4">
      <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-3">
        {events.length} tasks on {format(day, "dd MMM yyyy")}
      </div>
      <div className="space-y-2">
        {events.length === 0 && <div className="text-sm italic text-slate-400">No tasks scheduled.</div>}
        {events.map((ev) => (
          <EventChip key={ev.task_id} event={ev} onClick={onEventClick} />
        ))}
      </div>
    </div>
  );
}
